export interface StatusIncident {
  id: string;
  type: 'interruption' | 'slowdown' | 'error' | 'maintenance' | 'warning';
  affectedInstances: number;
  startTime: string;
  duration: number; // in seconds
  message: string;
  day?: number; // Day of the month when the incident occurred
  createdAt: string;
  finishedAt: string | null;
}

export interface DailyStatus {
  day: number;
  status: StatusType;
  incidents: StatusIncident[];
}

export interface InstanceStatus {
  id: string;
  name: string;
  status: StatusType;
  dailyStatus: DailyStatus[];
}

export interface ComponentStatus {
  id: string;
  name: string;
  type: 'service' | 'database' | 'other';
  status: StatusType;
  isCritical?: boolean;
  lastChecked: string;
  incidents: StatusIncident[];
  version?: string; // For version tracking
  instances: InstanceStatus[];
}

export interface SystemStatus {
  components: ComponentStatus[];
  lastUpdated: string;
  nextUpdateIn?: number; // Seconds until next update
} 

export type SystemType = 'dev' | 'prod';

import { PodMetric } from "../store/slices/podMetricsSlice";

export interface Service {
  id: number;
  name: string;
  status: string;
  maintenance: boolean;
  notes: string;
  isCritical: boolean;
  pods?: PodMetric[];
  isInMaintenanceList?: boolean; // Whether this service is in a maintenance list from the API
  containerName?: string; // The actual container_name from the pod
  loadingCritical?: boolean; // Whether the critical status is being updated
}

// Define valid status types to match the keys in statusTextColorMap
export type StatusType = 'Failed' | 'Pending' | 'Running' | 'Maintenance' | 'Outage' | 'Degraded' | 'Operational' | 'operational' | 'degraded' | 'outage' | 'maintenance' | 'warning' | 'pending' | 'failed' | 'running' | 'unknown' | 'degraded';

// Get status color for a specific day
export const getStatusColorForDay = (status?: string) => {
  if (!status) return 'bg-gray-300'; // No data
  
  switch (status) {
    case 'operational':
    case 'Running':
      return 'bg-[#F0FDF4]';
    case 'degraded':
    case 'Pending':
      return 'bg-[#FFFAEB]';
    case 'outage':
    case 'Failed':
      return 'bg-[#FEF3F2]';
    case 'maintenance':
    case 'Maintenance':
      return 'bg-[#EEF4FF]';
    case 'warning':
      return 'bg-[#FFFAEB]';
    default:
      return 'bg-white';
  }
};

// Get status badge text
export const getStatusBadgeText = (status: string) => {
  switch (status) {
    case 'outage':
    case 'Failed':
      return 'Outage';
    case 'degraded':
    case 'Pending':
      return 'Degraded';
    case 'maintenance':
    case 'Maintenance':
      return 'Maintenance';
    case 'operational':
    case 'Running':
      return 'Operational';
    default:
      return status;
  }
};

export const getStatusTextColor = (status: string) => {
  switch (status) {
    case 'outage':
    case 'Outage':
    case 'Failed':
      return 'text-red-500';
    case 'degraded':
    case 'Pending':
    case 'Degraded':
      return 'text-yellow-500';
    case 'maintenance':
    case 'Maintenance':
      return 'text-blue-500';
    case 'operational':
    case 'Running':
    case 'Operational':
      return 'text-green-500';
    default:
      return 'text-gray-500';
  }
};

// Color maps for status styling
export const statusTextColorMap = {
  'Failed': 'text-red-500',
  'Pending': 'text-yellow-500',
  'Running': 'text-green-500',
  'Maintenance': 'text-blue-500',
  'Outage': 'text-red-500',
  'Degraded': 'text-yellow-500',
  'Operational': 'text-green-500',
  'warning': 'text-yellow-500',
  'slowdown': 'text-yellow-500',
  'interruption': 'text-red-500',
  'error': 'text-red-500',
  'maintenance': 'text-blue-500',
  'operational': 'text-green-500',
  'degraded': 'text-yellow-500',
  'outage': 'text-red-500',
  'pending': 'text-yellow-500',
  'failed': 'text-red-500',
  'running': 'text-green-500',
  'unknown': 'text-gray-500'
};

export const statusBorderColorMap = {
  'Failed': 'border-red-500',
  'Pending': 'border-yellow-500',
  'Running': 'border-green-500',
  'Maintenance': 'border-blue-500',
  'Outage': 'border-red-500',
  'Degraded': 'border-yellow-500',
  'Operational': 'border-green-500',
  'warning': 'border-yellow-500',
  'slowdown': 'border-yellow-500',
  'interruption': 'border-red-500',
  'error': 'border-red-500',
  'maintenance': 'border-blue-500',
  'operational': 'border-green-500'
};

export const statusBgColorMap = {
  'Failed': 'bg-red-500',
  'Pending': 'bg-yellow-500',
  'Running': 'bg-green-500',
  'Maintenance': 'bg-blue-500',
  'Outage': 'bg-red-500',
  'Degraded': 'bg-yellow-500',
  'Operational': 'bg-green-500',
  'warning': 'bg-yellow-500',
  'slowdown': 'bg-yellow-500',
  'interruption': 'bg-red-500',
  'error': 'bg-red-500',
  'maintenance': 'bg-blue-500',
  'operational': 'bg-green-500'
};